import type { ParamSpec } from "../../api/admin";

/**
 * A job's settings form, generated from the parameters the backend declares.
 *
 * Nothing here knows what a job is. Adding a knob to a stage puts it on this
 * form by itself, which is the whole reason the parameters are data rather
 * than a hand-written form per job.
 */
export function ParamForm({
  specs, values, onChange, disabled,
}: {
  specs: ParamSpec[];
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  disabled?: boolean;
}) {
  if (specs.length === 0) {
    return <p className="dim small">This job takes no settings.</p>;
  }
  return (
    <div className="paramform">
      {specs.map((s) => (
        <label key={s.name} className="param">
          <span className="pname">{s.label}</span>
          <Field spec={s} value={values[s.name]} onChange={onChange} disabled={disabled} />
          {s.help && <span className="phelp">{s.help}</span>}
        </label>
      ))}
    </div>
  );
}

function Field({
  spec: s, value, onChange, disabled,
}: {
  spec: ParamSpec;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  disabled?: boolean;
}) {
  switch (s.kind) {
    case "bool":
      return (
        <input
          type="checkbox"
          disabled={disabled}
          checked={value === true}
          onChange={(e) => onChange(s.name, e.target.checked)}
        />
      );

    case "int":
    case "float":
      return (
        <input
          type="number"
          disabled={disabled}
          value={value === undefined || value === null ? "" : String(value)}
          min={s.min}
          max={s.max}
          step={s.kind === "int" ? 1 : "any"}
          onChange={(e) =>
            onChange(s.name, e.target.value === "" ? undefined : Number(e.target.value))
          }
        />
      );

    case "date":
      return (
        <input
          type="date"
          disabled={disabled}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(s.name, e.target.value)}
        />
      );

    case "strings": {
      // A fixed set of options is a set of checkboxes; anything else is a
      // comma-separated line, because the only free-form list here is a shop's
      // section names and there are two or three of them.
      const list = Array.isArray(value) ? (value as string[]) : [];
      if (s.options?.length) {
        return (
          <span className="opts">
            {s.options.map((o) => (
              <label key={o} className={`opt${list.includes(o) ? " on" : ""}`}>
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={list.includes(o)}
                  onChange={() =>
                    onChange(s.name, list.includes(o)
                      ? list.filter((x) => x !== o)
                      : [...list, o])
                  }
                />
                {o}
              </label>
            ))}
          </span>
        );
      }
      return (
        <input
          type="text"
          disabled={disabled}
          placeholder="comma separated"
          value={list.join(", ")}
          onChange={(e) =>
            onChange(s.name, e.target.value.split(",").map((x) => x.trim()).filter(Boolean))
          }
        />
      );
    }

    default:
      if (s.options?.length) {
        return (
          <select
            disabled={disabled}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(s.name, e.target.value)}
          >
            <option value="">—</option>
            {s.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      }
      return (
        <input
          type="text"
          disabled={disabled}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(s.name, e.target.value)}
        />
      );
  }
}
